import { invokeLater } from '../../../CodesThatsGottaGoSomewhereElse';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { MouseButtons, MouseInput } from '../../../platform/input/MouseInput';
import { UniversalSurfaceRenderer } from '../../../platform/UniversalSurfaceRenderer';
import { Colors } from '../../../ui';
import { Camera } from '../../canvas/Camera';
import { EditorSession } from '../../canvas/EditorSession';
import { RelativeTo } from '../../canvas/entities/Physical';
import { Physicals } from '../../canvas/entities/Physicals';
import { CommonShortcutToolBehavior } from '../../canvas/tooling/CommonShortcutToolBehavior';
import { PanZoomToolBehavior } from '../../canvas/tooling/PanZoomToolBehavior';
import { SlapToolBehavior } from '../../canvas/tooling/SlapToolBehavior';
import { ToolBehavior, ToolSettings } from '../../canvas/tooling/ToolBehavior';
import { toolReset } from '../../canvas/tooling/ToolCommands';
import { defineTool } from '../../canvas/tooling/ToolDefinition';
import { ToolOverlay } from '../../canvas/tooling/ToolOverlay';
import { SpatialIndex } from '../../canvas/utilities/SpatialIndex';
import { resolve } from '../../Container';
import { ProfileModel } from '../../ProfileModel';
import { tagsAdd, tagsToggleEdit } from './TaggingCommands';
import { TagSupport } from './TagSupport';

type TagRegion = { from?: Point, to?: Point };

export class TagToolBehavior extends ToolBehavior {

    @resolve(Camera) protected camera!: Camera;
    @resolve(CommandDispatcher) protected dispatch!: CommandDispatcher;
    @resolve(ProfileModel) protected me!: ProfileModel;
    @resolve(EditorSession) protected session!: EditorSession;
    @resolve(SpatialIndex) protected spatialIndex!: SpatialIndex;
    @resolve(ToolOverlay) private overlay!: ToolOverlay;

    private mousePosition = null as Point | null;
    private region = {} as TagRegion;

    private newName = '';
    private newValue = '';
    private newSymbol = '';
    private newColor = '';

    public init(settings: ToolSettings) {
        this.overlay.add(this.paintRegion.bind(this));
        this.newName = settings.name;
        this.newValue = settings.value;
        this.newSymbol = settings.symbol;
        this.newColor = settings.color;
    }

    public getPreferredCursor() {
        if (this.mousePosition) {
            const hits = this.findTaggablesAt(this.camera.toModel(this.mousePosition));
            if (hits.length > 0) {
                return 'tag';
            }
        }
        return 'not-allowed';
    }

    public onMouseDown(state: string, e: MouseInput): string | void {
        if (state === 'idle' && e.button === MouseButtons.Main) {
            if (e.targetPhysical?.is(TagSupport) || e.modifiers.shift) {
                this.beginTagging();
                return 'tagging';
            }
        }
    }

    public onMouseUp(state: string, mi: MouseInput): string | void {
        if (state === 'tagging') {
            this.completeTagging(mi.modifiers.shift);
            return 'idle';
        }
    }

    public onMouseMove(state: string, mi: MouseInput): string | void {
        this.mousePosition = mi.position;

        if (state === 'tagging') {
            this.updateRegion({ to: this.camera.toModel(mi.position) });
        }
    }

    protected beginTagging() {
        if (!this.mousePosition) return;
        const mpt = this.camera.toModel(this.mousePosition);
        this.updateRegion({ from: mpt });
    }

    protected completeTagging(repeat?: boolean) {

        const mpt = this.camera.toModel(this.mousePosition);
        this.updateRegion({ to: mpt });

        const targets = this.findTaggablesUsingRegion();

        if (targets.length > 0) {

            const ids = this.dispatch(tagsAdd, [
                this.newName,
                this.newValue,
                this.newSymbol,
                this.newColor,
                targets.map(x => x.id),
            ]);

            if (!repeat && targets.length === 1) {
                invokeLater(0, () => this.dispatch(tagsToggleEdit, [ids[0], true]));
            }
        }

        this.updateRegion(null);

        if (!repeat) {
            this.dispatch(toolReset);
        }
    }

    private findTaggablesUsingRegion() {
        // Only use a capture if the region is large enough
        const { from, to } = this.region;
        if (!from || !to) return [];

        const area = Rect.fromPoints([from, to]);
        const capture = from.distanceTo(to) > 5;
        return this.findTaggablesAt(capture ? area : from);
    }

    private findTaggablesAt(rectOrPoint: Rect | Point) {
        const hits = Physicals.polarize(this.spatialIndex.lookup(rectOrPoint), TagSupport);
        if (rectOrPoint instanceof Rect) {
            return hits.filter(x => rectOrPoint.contains(x.center(RelativeTo.Model)));
        }
        else {
            return hits;
        }
    }

    private updateRegion(value: Partial<TagRegion>) {
        this.region = !!value ? { ...this.region, ...value } : {};
        this.overlay.repaint();
    }

    private paintRegion(gfx: UniversalSurfaceRenderer) {
        const { from, to } = this.region ?? {};
        if (!from || !to) return;

        const hits = this.findTaggablesUsingRegion();

        const rect = Rect.fromPoints([
            this.camera.toView(from),
            this.camera.toView(to),
        ]);

        gfx.lineWidth = 4;
        gfx.strokeStyle = this.me.color ?? Colors.primary;
        gfx.strokeRoundRect(rect.left + 2, rect.top + 2, rect.width - 4, rect.height - 4, 3);

        for (const tp of hits) {
            const r = Rect.fromPoints(this.camera.toView(tp.bounds(RelativeTo.Model)));
            gfx.strokeRect(r.left, r.top, r.width, r.height);
        }
    }
}

export const tagTool = defineTool({
    id: 'tagTool',
    label: 'Tag',
    category: 'collaboration',
    // icon: 'deqo://icons/hero/tag',
    weight: 4010,
    behaviors: [
        CommonShortcutToolBehavior,
        TagToolBehavior,
        PanZoomToolBehavior(),
        SlapToolBehavior,
    ]
});
